import { useEffect, useState, useMemo, useCallback } from "react";
import { Plus, Trash2, Target, Loader2 } from "lucide-react";
import { format, addDays, isSameDay, startOfDay, addMonths, startOfMonth } from "date-fns";
import api from "@/lib/api";
import { toast } from "sonner";
import FocusHeader from "@/components/FocusHeader";
import { TID } from "@/lib/testIds";
import { useRealtimeChannel } from "@/context/RealtimeContext";
import EventDetailModal from "@/components/EventDetailModal";

/**
 * Operations focus — designed to fit on a single 60" TV screen.
 * Layout:
 *   left 68%  : 2-week schedule laid out as a 2 x 7 grid (this week / next week)
 *   right 32% : three-month goals stacked vertically
 */

const CATEGORY_COLOR = {
    task: "#FF5500",
    meeting: "#2979FF",
    training: "#FFEA00",
    ops: "#00E676",
};

const GOAL_COLOR = {
    goal: "#FF5500",
    event: "#2979FF",
    milestone: "#FFEA00",
};

const GOAL_STATUS = {
    planned: { bg: "#333333", fg: "#A1A1AA", label: "PLANNED" },
    "in-progress": { bg: "#FFEA00", fg: "#050505", label: "IN PROGRESS" },
    achieved: { bg: "#00E676", fg: "#050505", label: "ACHIEVED" },
    dropped: { bg: "#FF1744", fg: "#FFFFFF", label: "DROPPED" },
};

export default function Operations() {
    const [schedule, setSchedule] = useState([]);
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showGoalModal, setShowGoalModal] = useState(false);
    const [editSchedule, setEditSchedule] = useState(null);
    const [editGoal, setEditGoal] = useState(null);
    const [detailEvent, setDetailEvent] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, g] = await Promise.all([
                api.get("/operations/schedule"),
                api.get("/operations/goals"),
            ]);
            setSchedule(s.data);
            setGoals(g.data);
        } catch (e) {
            toast.error("Failed to load operations.");
        } finally {
            setLoading(false);
        }
    }, []);

    useRealtimeChannel("operations", load);

    useEffect(() => {
        load();
    }, [load]);

    const days = useMemo(() => {
        const start = startOfDay(new Date());
        return Array.from({ length: 14 }).map((_, i) => addDays(start, i));
    }, []);

    const months = useMemo(() => {
        const start = startOfMonth(new Date());
        return Array.from({ length: 3 }).map((_, i) => addMonths(start, i));
    }, []);

    const scheduleByDay = useMemo(() => {
        const map = {};
        for (const it of schedule) {
            const startStr = it.date;
            const endStr = it.end_date || it.date;
            if (!startStr) continue;
            // Expand each day in [startStr, endStr] inclusive.
            let cursor = startOfDay(new Date(startStr + "T00:00:00"));
            const end = startOfDay(new Date(endStr + "T00:00:00"));
            while (cursor <= end) {
                const key = format(cursor, "yyyy-MM-dd");
                if (!map[key]) map[key] = [];
                map[key].push({
                    ...it,
                    _isStart: key === startStr,
                    _isEnd: key === endStr,
                    _spans: startStr !== endStr,
                });
                cursor = addDays(cursor, 1);
            }
        }
        return map;
    }, [schedule]);

    const goalsByMonth = useMemo(() => {
        const map = { 0: [], 1: [], 2: [] };
        for (const g of goals) {
            const off = g.month_offset ?? 0;
            if (map[off]) map[off].push(g);
        }
        return map;
    }, [goals]);

    const saveSchedule = async (data) => {
        try {
            if (editSchedule?.id) {
                const { data: u } = await api.patch(
                    `/operations/schedule/${editSchedule.id}`,
                    data
                );
                setSchedule((prev) => prev.map((x) => (x.id === u.id ? u : x)));
            } else {
                const { data: c } = await api.post("/operations/schedule", data);
                setSchedule((prev) => [...prev, c]);
            }
            setShowScheduleModal(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed.");
        }
    };

    const deleteSchedule = async (id) => {
        try {
            await api.delete(`/operations/schedule/${id}`);
            setSchedule((prev) => prev.filter((x) => x.id !== id));
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const saveGoal = async (data) => {
        try {
            if (editGoal?.id) {
                const { data: u } = await api.patch(`/operations/goals/${editGoal.id}`, data);
                setGoals((prev) => prev.map((x) => (x.id === u.id ? u : x)));
            } else {
                const { data: c } = await api.post("/operations/goals", data);
                setGoals((prev) => [...prev, c]);
            }
            setShowGoalModal(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed.");
        }
    };

    const deleteGoal = async (id) => {
        try {
            await api.delete(`/operations/goals/${id}`);
            setGoals((prev) => prev.filter((x) => x.id !== id));
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const cycleGoalStatus = async (goal) => {
        const order = ["planned", "in-progress", "achieved", "dropped"];
        const next = order[(order.indexOf(goal.status) + 1) % order.length];
        try {
            const { data: u } = await api.patch(`/operations/goals/${goal.id}`, {
                status: next,
            });
            setGoals((prev) => prev.map((x) => (x.id === u.id ? u : x)));
        } catch (e) {
            toast.error("Update failed.");
        }
    };

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            <FocusHeader
                code="AO/02"
                title="Operations"
                subtitle="14-day tactical schedule · 90-day strategic outlook."
            />

            {loading ? (
                <div className="flex-1 flex items-center justify-center text-virtus-dim">
                    <Loader2 className="animate-spin" />
                </div>
            ) : (
                <div className="flex-1 grid grid-cols-1 md:grid-cols-[68%_32%] gap-px bg-virtus-line md:overflow-hidden">
                    {/* LEFT: 2-week schedule as 2×7 grid */}
                    <div className="bg-virtus-bg flex flex-col md:overflow-hidden">
                        <SectionHeader
                            code="TACTICAL // 14-DAY"
                            title="Two-Week Schedule"
                            action={
                                <button
                                    onClick={() => {
                                        setEditSchedule({
                                            date: format(new Date(), "yyyy-MM-dd"),
                                            time: "09:00",
                                            title: "",
                                            category: "task",
                                        });
                                        setShowScheduleModal(true);
                                    }}
                                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                                    data-testid={TID.opsScheduleAddBtn}
                                >
                                    <Plus size={11} /> ADD TASK
                                </button>
                            }
                        />

                        {/* Row labels — WEEK 01 / WEEK 02. On mobile, allow
                            horizontal scroll so the 7-day columns keep their
                            width instead of squeezing into unreadable stripes. */}
                        <div className="flex-1 grid grid-rows-2 gap-px bg-virtus-line overflow-auto md:overflow-hidden">
                            {[0, 1].map((weekIdx) => (
                                <div
                                    key={weekIdx}
                                    className="bg-virtus-bg grid grid-cols-[36px_repeat(7,minmax(80px,1fr))] gap-px min-w-[600px] md:min-w-0"
                                >
                                    <div className="flex items-center justify-center bg-virtus-surface font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint [writing-mode:vertical-rl] rotate-180">
                                        WEEK 0{weekIdx + 1}
                                    </div>
                                    {days
                                        .slice(weekIdx * 7, weekIdx * 7 + 7)
                                        .map((d) => {
                                            const key = format(d, "yyyy-MM-dd");
                                            const rows = (scheduleByDay[key] || []).sort(
                                                (a, b) => (a.time || "").localeCompare(b.time || "")
                                            );
                                            const isToday = isSameDay(d, new Date());
                                            return (
                                                <div
                                                    key={key}
                                                    className={`flex flex-col overflow-hidden ${
                                                        isToday
                                                            ? "bg-[#141414]"
                                                            : "bg-virtus-bg"
                                                    }`}
                                                    style={
                                                        isToday
                                                            ? {
                                                                  boxShadow:
                                                                      "inset 0 0 32px rgba(255, 85, 0, 0.18), inset 0 0 0 2px rgba(255, 85, 0, 0.9), 0 0 12px rgba(255, 85, 0, 0.35)",
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    <div
                                                        className={`flex items-baseline justify-between px-2 py-1 border-b ${
                                                            isToday
                                                                ? "border-virtus-orange"
                                                                : "border-virtus-line"
                                                        }`}
                                                    >
                                                        <div>
                                                            <div
                                                                className={`font-mono text-[9px] tracking-[0.22em] uppercase ${
                                                                    isToday
                                                                        ? "text-virtus-orange"
                                                                        : "text-virtus-faint"
                                                                }`}
                                                            >
                                                                {format(d, "EEE")}
                                                            </div>
                                                            <div
                                                                className={`font-heading font-black text-lg leading-none ${
                                                                    isToday
                                                                        ? "text-virtus-orange"
                                                                        : "text-white"
                                                                }`}
                                                            >
                                                                {format(d, "dd")}
                                                            </div>
                                                        </div>
                                                        <div className="font-mono text-[8px] tracking-[0.22em] uppercase text-virtus-faint">
                                                            {format(d, "MMM")}
                                                        </div>
                                                        {isToday && (
                                                            <div className="w-1.5 h-1.5 bg-virtus-orange pulse-dot" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1 overflow-y-auto py-1 space-y-0.5" data-kiosk-scroll="true">
                                                        {rows.length === 0 && (
                                                            <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-virtus-faint px-2">
                                                                &gt; open
                                                            </div>
                                                        )}
                                                        {rows.map((r) => {
                                                            const catColor =
                                                                CATEGORY_COLOR[r.category] || "#FF5500";
                                                            const isMulti = r._spans;
                                                            const isMiddle =
                                                                isMulti && !r._isStart && !r._isEnd;
                                                            const isEndOnly =
                                                                isMulti && r._isEnd && !r._isStart;

                                                            // Multi-day continuation: compact bar that bleeds to
                                                            // cell edges so the colored strip visually stitches
                                                            // across day cells.
                                                            if (isMiddle || isEndOnly) {
                                                                return (
                                                                    <div
                                                                        key={r.id}
                                                                        onClick={() => setDetailEvent(r)}
                                                                        className="group cursor-pointer flex items-center gap-1.5 px-2 py-1"
                                                                        style={{
                                                                            backgroundColor: `${catColor}33`,
                                                                            borderTop: `1px solid ${catColor}99`,
                                                                            borderBottom: `1px solid ${catColor}99`,
                                                                            borderRight: isEndOnly
                                                                                ? `2px solid ${catColor}`
                                                                                : undefined,
                                                                        }}
                                                                        title={`${r.title}  (${r.date} → ${r.end_date})`}
                                                                        data-testid={TID.opsScheduleRow(r.id)}
                                                                    >
                                                                        <span
                                                                            className="font-mono text-[10px] flex-shrink-0"
                                                                            style={{ color: catColor }}
                                                                        >
                                                                            {isEndOnly ? "▪" : "→"}
                                                                        </span>
                                                                        <span className="text-[11px] text-white/85 truncate leading-tight flex-1">
                                                                            {r.title}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            }

                                                            // Start day (or single-day): full card with title,
                                                            // time, and category color bar. When it's the start
                                                            // of a multi-day event we also tint the background
                                                            // and drop the right border so the strip continues
                                                            // seamlessly into the next day cell.
                                                            return (
                                                                <div
                                                                    key={r.id}
                                                                    onClick={() => setDetailEvent(r)}
                                                                    style={{
                                                                        boxShadow: `inset 2px 0 0 ${catColor}`,
                                                                        backgroundColor: isMulti
                                                                            ? `${catColor}33`
                                                                            : undefined,
                                                                        borderTop: isMulti
                                                                            ? `1px solid ${catColor}99`
                                                                            : undefined,
                                                                        borderBottom: isMulti
                                                                            ? `1px solid ${catColor}99`
                                                                            : undefined,
                                                                    }}
                                                                    className={`group px-2 py-1 cursor-pointer ${
                                                                        isMulti
                                                                            ? "hover:brightness-125"
                                                                            : "border border-virtus-line bg-virtus-surface hover:border-virtus-orange"
                                                                    }`}
                                                                    data-testid={TID.opsScheduleRow(r.id)}
                                                                >
                                                                    <div className="flex items-center gap-1 font-mono text-[9px] text-virtus-orange">
                                                                        {isMulti && (
                                                                            <span
                                                                                title={`${r.date} → ${r.end_date}`}
                                                                                style={{ color: catColor }}
                                                                            >
                                                                                ▸
                                                                            </span>
                                                                        )}
                                                                        {r.time || "—"}
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                deleteSchedule(r.id);
                                                                            }}
                                                                            className="ml-auto opacity-0 group-hover:opacity-100 text-virtus-faint hover:text-virtus-stuck"
                                                                            aria-label="Delete"
                                                                        >
                                                                            <Trash2 size={10} />
                                                                        </button>
                                                                    </div>
                                                                    <div className="text-[11px] text-white truncate leading-tight">
                                                                        {r.title}
                                                                    </div>
                                                                    {r.owner && (
                                                                        <div className="font-mono text-[9px] text-virtus-dim truncate">
                                                                            {r.owner}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: 3-month goals — three stacked cards */}
                    <div className="bg-virtus-bg flex flex-col md:overflow-hidden">
                        <SectionHeader
                            code="STRATEGIC // 90-DAY"
                            title="Goals & Events"
                            action={
                                <button
                                    onClick={() => {
                                        setEditGoal({
                                            title: "",
                                            month_offset: 0,
                                            category: "goal",
                                        });
                                        setShowGoalModal(true);
                                    }}
                                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                                    data-testid={TID.opsGoalAddBtn}
                                >
                                    <Plus size={11} /> ADD
                                </button>
                            }
                        />
                        <div className="flex-1 grid grid-rows-3 gap-px bg-virtus-line md:overflow-hidden min-h-[600px] md:min-h-0">
                            {months.map((m, idx) => {
                                const rows = goalsByMonth[idx] || [];
                                return (
                                    <div
                                        key={m.toISOString()}
                                        className="bg-virtus-bg flex flex-col overflow-hidden"
                                    >
                                        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-virtus-line">
                                            <Target size={12} className="text-virtus-orange" />
                                            <div className="font-heading font-bold uppercase tracking-tight text-sm">
                                                {format(m, "MMMM yyyy")}
                                            </div>
                                            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-faint ml-auto">
                                                [{rows.length}]
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1" data-kiosk-scroll="true">
                                            {rows.length === 0 && (
                                                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-virtus-faint">
                                                    &gt; No goals
                                                </div>
                                            )}
                                            {rows.map((r) => {
                                                const st =
                                                    GOAL_STATUS[r.status] || GOAL_STATUS.planned;
                                                return (
                                                    <div
                                                        key={r.id}
                                                        style={{
                                                            boxShadow: `inset 2px 0 0 ${
                                                                GOAL_COLOR[r.category] || "#FF5500"
                                                            }`,
                                                        }}
                                                        onClick={() => {
                                                            setEditGoal(r);
                                                            setShowGoalModal(true);
                                                        }}
                                                        className="group flex items-center gap-2 px-2 py-1 border border-virtus-line bg-virtus-surface hover:border-virtus-orange cursor-pointer"
                                                        data-testid={TID.opsGoalRow(r.id)}
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-xs text-white truncate leading-tight">
                                                                {r.title}
                                                            </div>
                                                            <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-virtus-faint truncate">
                                                                {r.category?.toUpperCase()}
                                                                {r.target_date && r.end_date && r.end_date !== r.target_date
                                                                    ? ` · ${r.target_date} → ${r.end_date}`
                                                                    : r.target_date && ` · ${r.target_date}`}
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                cycleGoalStatus(r);
                                                            }}
                                                            className="px-2 py-0.5 font-mono text-[9px] font-bold tracking-[0.16em]"
                                                            style={{
                                                                backgroundColor: st.bg,
                                                                color: st.fg,
                                                            }}
                                                        >
                                                            {st.label}
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteGoal(r.id);
                                                            }}
                                                            className="opacity-0 group-hover:opacity-100 text-virtus-faint hover:text-virtus-stuck"
                                                            aria-label="Delete"
                                                        >
                                                            <Trash2 size={11} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {detailEvent && (
                <EventDetailModal
                    event={detailEvent}
                    onClose={() => setDetailEvent(null)}
                    onEdit={() => {
                        setEditSchedule(detailEvent);
                        setShowScheduleModal(true);
                        setDetailEvent(null);
                    }}
                    onDelete={async (ev) => {
                        await deleteSchedule(ev.id);
                        setDetailEvent(null);
                    }}
                />
            )}
            {showScheduleModal && (
                <ScheduleModal
                    item={editSchedule}
                    onClose={() => setShowScheduleModal(false)}
                    onSave={saveSchedule}
                />
            )}
            {showGoalModal && (
                <GoalModal
                    item={editGoal}
                    onClose={() => setShowGoalModal(false)}
                    onSave={saveGoal}
                />
            )}
        </div>
    );
}

function SectionHeader({ code, title, action }) {
    return (
        <div className="flex items-center justify-between px-4 py-2 border-b border-virtus-line flex-shrink-0">
            <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                    {code}
                </div>
                <div className="font-heading text-lg font-black uppercase tracking-tight leading-none mt-0.5">
                    {title}
                </div>
            </div>
            {action}
        </div>
    );
}

function ScheduleModal({ item, onClose, onSave }) {
    const [f, setF] = useState(item || {});
    const [multiDay, setMultiDay] = useState(
        !!(item?.end_date && item.end_date !== item.date)
    );
    const [micro, setMicro] = useState(item?.micro_schedule || []);
    const [org, setOrg] = useState(item?.task_org || []);
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

    const toggleMultiDay = () => {
        setMultiDay((prev) => {
            const next = !prev;
            if (!next) set("end_date", null);
            return next;
        });
    };

    const addMicro = () =>
        setMicro((p) => [...p, { id: `new-${Date.now()}-${p.length}`, time: "", title: "", owner: "", notes: "" }]);
    const updMicro = (i, key, val) =>
        setMicro((p) => p.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
    const rmMicro = (i) => setMicro((p) => p.filter((_, idx) => idx !== i));

    const addOrg = () =>
        setOrg((p) => [
            ...p,
            {
                id: `new-${Date.now()}-${p.length}`,
                name: "",
                role: "",
                responsibility: "",
                instructions: "",
            },
        ]);
    const updOrg = (i, key, val) =>
        setOrg((p) => p.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
    const rmOrg = (i) => setOrg((p) => p.filter((_, idx) => idx !== i));

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md">
            <div className="w-[900px] max-w-[94vw] max-h-[92vh] overflow-hidden bg-[#0A0A0A] border-2 border-virtus-orange flex flex-col">
                <div className="px-5 py-3 border-b border-virtus-line font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange flex-shrink-0">
                    {item?.id ? "EDIT EVENT" : "NEW EVENT"}
                </div>
                <div className="p-5 space-y-3 overflow-y-auto flex-1">
                    <Field label="Title">
                        <input
                            value={f.title || ""}
                            onChange={(e) => set("title", e.target.value)}
                            placeholder="Task 1"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            autoFocus
                        />
                    </Field>

                    {/* Multi-day toggle */}
                    <div className="flex items-center justify-between border border-virtus-line px-3 py-2 bg-[#0A0A0A]">
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim">
                            Multi-day event
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={multiDay}
                            onClick={toggleMultiDay}
                            data-testid="ops-schedule-multiday-toggle"
                            className={`relative w-11 h-5 border transition-colors ${
                                multiDay
                                    ? "bg-virtus-orange border-virtus-orange"
                                    : "bg-transparent border-virtus-line"
                            }`}
                        >
                            <span
                                className={`absolute top-0.5 h-4 w-4 transition-transform ${
                                    multiDay
                                        ? "translate-x-6 bg-black"
                                        : "translate-x-0.5 bg-virtus-dim"
                                }`}
                            />
                        </button>
                    </div>

                    <div className={`grid gap-3 ${multiDay ? "grid-cols-3" : "grid-cols-2"}`}>
                        <Field label="Start date">
                            <input
                                type="date"
                                value={f.date || ""}
                                onChange={(e) => set("date", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                                data-testid="ops-schedule-start-date"
                            />
                        </Field>
                        {multiDay && (
                            <Field label="End date">
                                <input
                                    type="date"
                                    value={f.end_date || ""}
                                    min={f.date || undefined}
                                    onChange={(e) => set("end_date", e.target.value || null)}
                                    className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                                    data-testid="ops-schedule-end-date"
                                />
                            </Field>
                        )}
                        <Field label="Time">
                            <input
                                type="time"
                                value={f.time || ""}
                                onChange={(e) => set("time", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Category">
                            <select
                                value={f.category || "task"}
                                onChange={(e) => set("category", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {["task", "meeting", "training", "ops"].map((c) => (
                                    <option key={c} value={c}>{c.toUpperCase()}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Owner">
                            <input
                                value={f.owner || ""}
                                onChange={(e) => set("owner", e.target.value)}
                                placeholder="Owner"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            />
                        </Field>
                    </div>
                    <Field label="Notes">
                        <textarea
                            value={f.notes || ""}
                            onChange={(e) => set("notes", e.target.value)}
                            rows={3}
                            placeholder="Free-form notes about the event"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm resize-none"
                        />
                    </Field>

                    {/* Micro-schedule editor */}
                    <div className="border border-virtus-line p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                                Micro-Schedule <span className="text-virtus-faint">[{micro.length}]</span>
                            </div>
                            <button
                                type="button"
                                onClick={addMicro}
                                className="flex items-center gap-1 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange hover:underline"
                                data-testid="ops-add-micro-btn"
                            >
                                <Plus size={11} /> ADD BLOCK
                            </button>
                        </div>
                        {micro.length === 0 ? (
                            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                                &gt; No sub-blocks yet.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {micro.map((b, i) => (
                                    <div key={b.id || i} className="grid grid-cols-[92px_1fr_140px_28px] gap-2 items-start">
                                        <input
                                            type="time"
                                            value={b.time || ""}
                                            onChange={(e) => updMicro(i, "time", e.target.value)}
                                            className="bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-2 py-1.5 text-white text-xs font-mono outline-none"
                                        />
                                        <input
                                            value={b.title || ""}
                                            onChange={(e) => updMicro(i, "title", e.target.value)}
                                            placeholder="Sub-block title"
                                            className="bg-transparent border border-virtus-line focus:border-virtus-orange px-2 py-1.5 text-white text-sm outline-none"
                                        />
                                        <input
                                            value={b.owner || ""}
                                            onChange={(e) => updMicro(i, "owner", e.target.value)}
                                            placeholder="Assigned to"
                                            className="bg-transparent border border-virtus-line focus:border-virtus-orange px-2 py-1.5 text-white text-sm outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => rmMicro(i)}
                                            className="text-virtus-faint hover:text-virtus-stuck flex items-center justify-center h-full"
                                            title="Remove"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Task Org editor */}
                    <div className="border border-virtus-line p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                                Task Org <span className="text-virtus-faint">[{org.length}]</span>
                            </div>
                            <button
                                type="button"
                                onClick={addOrg}
                                className="flex items-center gap-1 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange hover:underline"
                                data-testid="ops-add-org-btn"
                            >
                                <Plus size={11} /> ADD ROW
                            </button>
                        </div>
                        {org.length === 0 ? (
                            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint">
                                &gt; No task org yet.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {org.map((r, i) => (
                                    <div key={r.id || i} className="border border-virtus-line p-2 space-y-2">
                                        <div className="grid grid-cols-[1fr_1fr_28px] gap-2">
                                            <input
                                                value={r.name || ""}
                                                onChange={(e) => updOrg(i, "name", e.target.value)}
                                                placeholder="Name"
                                                className="bg-transparent border border-virtus-line focus:border-virtus-orange px-2 py-1.5 text-white text-sm outline-none"
                                            />
                                            <input
                                                value={r.role || ""}
                                                onChange={(e) => updOrg(i, "role", e.target.value)}
                                                placeholder="Role (e.g., Coxswain)"
                                                className="bg-transparent border border-virtus-line focus:border-virtus-orange px-2 py-1.5 text-white text-sm outline-none"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => rmOrg(i)}
                                                className="text-virtus-faint hover:text-virtus-stuck flex items-center justify-center"
                                                title="Remove"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <textarea
                                                value={r.responsibility || ""}
                                                onChange={(e) => updOrg(i, "responsibility", e.target.value)}
                                                placeholder="Responsibility"
                                                rows={3}
                                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-2 py-1.5 text-white text-sm outline-none resize-none"
                                            />
                                            <textarea
                                                value={r.instructions || ""}
                                                onChange={(e) => updOrg(i, "instructions", e.target.value)}
                                                placeholder="Detailed instructions"
                                                rows={3}
                                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-2 py-1.5 text-white text-sm outline-none resize-none"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-virtus-line flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim px-3 py-2"
                    >
                        CANCEL
                    </button>
                    <button
                        onClick={() =>
                            f.title?.trim() &&
                            f.date &&
                            onSave({
                                title: f.title.trim(),
                                date: f.date,
                                end_date:
                                    multiDay && f.end_date && f.end_date >= f.date
                                        ? f.end_date
                                        : null,
                                time: f.time || null,
                                category: f.category || "task",
                                owner: f.owner || null,
                                notes: f.notes || "",
                                micro_schedule: micro
                                    .filter((b) => (b.title || "").trim())
                                    .map((b) => ({
                                        id: String(b.id).startsWith("new-") ? undefined : b.id,
                                        time: b.time || null,
                                        title: (b.title || "").trim(),
                                        owner: b.owner || null,
                                        notes: b.notes || null,
                                    })),
                                task_org: org
                                    .filter((r) => (r.name || "").trim())
                                    .map((r) => ({
                                        id: String(r.id).startsWith("new-") ? undefined : r.id,
                                        name: (r.name || "").trim(),
                                        role: r.role || null,
                                        responsibility: r.responsibility || null,
                                        instructions: r.instructions || null,
                                    })),
                            })
                        }
                        className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange"
                    >
                        SAVE
                    </button>
                </div>
            </div>
        </div>
    );
}

function GoalModal({ item, onClose, onSave }) {
    const [f, setF] = useState(item || {});
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md">
            <div className="w-[460px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="px-5 py-3 border-b border-virtus-line font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                    {item?.id ? "EDIT GOAL" : "NEW GOAL"}
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Title">
                        <input
                            value={f.title || ""}
                            onChange={(e) => set("title", e.target.value)}
                            placeholder="Goal 1"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            autoFocus
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Month">
                            <select
                                value={f.month_offset ?? 0}
                                onChange={(e) => set("month_offset", parseInt(e.target.value, 10))}
                                className="w-full bg-[#0A0A0A] border border-virtus-line px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                <option value={0}>THIS MONTH</option>
                                <option value={1}>NEXT MONTH</option>
                                <option value={2}>MONTH +2</option>
                            </select>
                        </Field>
                        <Field label="Category">
                            <select
                                value={f.category || "goal"}
                                onChange={(e) => set("category", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {["goal", "event", "milestone"].map((c) => (
                                    <option key={c} value={c}>{c.toUpperCase()}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Start / target date">
                            <input
                                type="date"
                                value={f.target_date || ""}
                                onChange={(e) => set("target_date", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                                data-testid="ops-goal-start-date"
                            />
                        </Field>
                        <Field label="End date (opt.)">
                            <input
                                type="date"
                                value={f.end_date || ""}
                                min={f.target_date || undefined}
                                onChange={(e) => set("end_date", e.target.value || null)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                                data-testid="ops-goal-end-date"
                            />
                        </Field>
                    </div>
                    <Field label="Notes">
                        <textarea
                            value={f.notes || ""}
                            onChange={(e) => set("notes", e.target.value)}
                            rows={2}
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </Field>
                </div>
                <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-virtus-line">
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim px-3 py-2"
                    >
                        CANCEL
                    </button>
                    <button
                        onClick={() =>
                            f.title?.trim() &&
                            onSave({
                                title: f.title.trim(),
                                month_offset: f.month_offset ?? 0,
                                category: f.category || "goal",
                                target_date: f.target_date || null,
                                end_date: f.end_date && (!f.target_date || f.end_date >= f.target_date) ? f.end_date : null,
                                notes: f.notes || "",
                            })
                        }
                        className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange"
                    >
                        SAVE
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}
